import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgxChartsModule, Color, ScaleType } from '@swimlane/ngx-charts';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AnalyticsService } from '../../../../services/analytics.service';
import { AnalyticsFilters } from '../../../../models/analytics.models';

/**
 * Supply vs Demand Chart Component
 * Displays market balance with jobs vs candidates metrics and interpretation badges
 * SSR-safe: Only renders charts in browser context
 */
@Component({
  selector: 'app-supply-demand-chart',
  standalone: true,
  imports: [CommonModule, NgxChartsModule, MatProgressSpinnerModule, MatCardModule, MatChipsModule, TranslocoModule],
  templateUrl: './supply-demand-chart.component.html',
  styleUrls: ['./supply-demand-chart.component.scss'],
})
export class SupplyDemandChartComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  private readonly platformId = inject(PLATFORM_ID);
  readonly isBrowser = isPlatformBrowser(this.platformId);

  @Input() filters!: AnalyticsFilters;

  // Raw API data storage for re-transformation on language change
  private rawData: any = null;

  // Metric cards data
  availableJobs = 0;
  availableCandidates = 0;
  supplyDemandRatio = 0;
  interpretation: 'supply_exceeds_demand' | 'balanced' | 'demand_exceeds_supply' = 'balanced';

  // Breakdown data
  categoryData: Array<{
    name: string;
    jobs: number;
    candidates: number;
    ratio: number;
    interpretation: string;
  }> = [];
  positionData: Array<{
    name: string;
    jobs: number;
    candidates: number;
    ratio: number;
    interpretation: string;
  }> = [];
  locationData: Array<{
    name: string;
    jobs: number;
    candidates: number;
    ratio: number;
    interpretation: string;
  }> = [];

  loading = false;
  error: string | null = null;

  constructor(private readonly analyticsService: AnalyticsService, private readonly translocoService: TranslocoService) {}

  ngOnInit(): void {
    // Only load chart data on browser
    if (this.isBrowser && this.filters) {
      this.loadChartData();
    }

    // Subscribe to language changes and re-transform data
    this.translocoService.langChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.rawData) {
          this.transformAllData();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload data when filters change
    if (changes['filters'] && !changes['filters'].firstChange && this.isBrowser) {
      this.loadChartData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load chart data from API
   */
  private loadChartData(): void {
    if (!this.filters) {
      return;
    }

    this.loading = true;
    this.error = null;

    this.analyticsService
      .getSupplyDemand(this.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          // Store raw data for language change re-transformation
          this.rawData = data;
          // Transform all data with current language
          this.transformAllData();
          this.loading = false;
        },
        error: (err) => {
          console.error('Chart data error:', err);
          this.error = this.translocoService.translate('analytics.errorLoadChartData');
          this.loading = false;
        },
      });
  }

  /**
   * Transform all data with current language
   * Called on initial load and when language changes
   */
  private transformAllData(): void {
    if (!this.rawData) {
      return;
    }

    // Set metric cards
    this.availableJobs = this.rawData.availableJobs;
    this.availableCandidates = this.rawData.availableCandidates;
    this.supplyDemandRatio = this.rawData.supplyDemandRatio;
    this.interpretation = this.rawData.interpretation;

    // Transform breakdown data
    this.categoryData = this.transformCategoryData(this.rawData.breakdown.byCategory);
    this.positionData = this.transformPositionData(this.rawData.breakdown.byPosition);
    this.locationData = this.transformLocationData(this.rawData.breakdown.byLocation);
  }

  /**
   * Transform category breakdown data
   * categoryName is actually the categoryKey (e.g., "top_management")
   */
  private transformCategoryData(
    categories: Array<{
      categoryId: number;
      categoryName: string;
      jobs: number;
      candidates: number;
      ratio: number;
      interpretation: string;
    }>
  ): Array<{ name: string; jobs: number; candidates: number; ratio: number; interpretation: string }> {
    if (!categories || categories.length === 0) {
      return [];
    }

    return categories.map((item) => ({
      name: this.translocoService.translate(`jobCategories.${item.categoryName}`),
      jobs: item.jobs,
      candidates: item.candidates,
      ratio: item.ratio,
      interpretation: item.interpretation,
    }));
  }

  /**
   * Transform position breakdown data
   * positionName is actually the positionKey (e.g., "manager")
   */
  private transformPositionData(
    positions: Array<{
      positionId: number;
      positionName: string;
      jobs: number;
      candidates: number;
      ratio: number;
      interpretation: string;
    }>
  ): Array<{ name: string; jobs: number; candidates: number; ratio: number; interpretation: string }> {
    if (!positions || positions.length === 0) {
      return [];
    }

    return positions.map((item) => ({
      name: this.translocoService.translate(`jobPositions.${item.positionName}`),
      jobs: item.jobs,
      candidates: item.candidates,
      ratio: item.ratio,
      interpretation: item.interpretation,
    }));
  }

  /**
   * Transform location breakdown data
   */
  private transformLocationData(
    locations: Array<{
      city: string;
      country: string;
      jobs: number;
      candidates: number;
      ratio: number;
      interpretation: string;
    }>
  ): Array<{ name: string; jobs: number; candidates: number; ratio: number; interpretation: string }> {
    if (!locations || locations.length === 0) {
      return [];
    }

    return locations.map((item) => {
      // Translate location name if it's a special key like "Others"
      const cityName = item.city === 'Others' ? this.translocoService.translate(`locations.${item.city}`) : item.city;
      const name = item.country ? `${cityName}, ${item.country}` : cityName;
      return {
        name,
        jobs: item.jobs,
        candidates: item.candidates,
        ratio: item.ratio,
        interpretation: item.interpretation,
      };
    });
  }

  /**
   * Get CSS class for interpretation badge
   */
  getInterpretationClass(interpretation: string): string {
    switch (interpretation) {
      case 'supply_exceeds_demand':
        return 'badge-green';
      case 'balanced':
        return 'badge-blue';
      case 'demand_exceeds_supply':
        return 'badge-orange';
      default:
        return 'badge-gray';
    }
  }

  /**
   * Get display text for interpretation
   */
  getInterpretationText(interpretation: string): string {
    return this.translocoService.translate(`analytics.interpretationValues.${interpretation}`);
  }
}
